import React from 'react';
import Bowser from 'bowser';
import classNames from 'classnames';
import { Transition } from '@headlessui/react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import type { IntlMessage } from 'modules/notifications/types/Notification';
import {
  InformationCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, CheckCircleIcon,
} from '@heroicons/react/24/solid';

import {
  NotificationType, Collapse, isIntlMessage,
} from 'modules/notifications/types/Notification';

import { TimeoutResult } from 'types/common';
import { isLowPerfMode } from 'modules/app/selectors';

import Icon from './Icon';
import { useSelector } from '../../hooks';

export interface AlertProps {
  title?: IntlMessage | React.ReactNode;
  description?: IntlMessage | React.ReactNode;
  collapse?: Collapse;
  duration?: number;
  type?: NotificationType;
  onClose?: () => void;
}

const ALERT_CONFIG = {
  [NotificationType.INFO]: {
    icon: <InformationCircleIcon className="h-5 w-5 text-brand-default" />,
  },
  [NotificationType.ERROR]: {
    icon: <ExclamationCircleIcon className="h-5 w-5 text-danger-default" />,
  },
  [NotificationType.WARNING]: {
    icon: <ExclamationTriangleIcon className="h-5 w-5 text-warning-default" />,
  },
  [NotificationType.SUCCESS]: {
    icon: <CheckCircleIcon className="h-5 w-5 text-success-default" />,
  },
};

export const descriptionFormatter = ({ values = {}, ...descriptor }) => ({
  ...descriptor,
  values: Object.entries(values).reduce((acc, [field, value]) => {
    const formattedValue = typeof value === 'string' && Date.parse(value) ? (
      <FormattedDate value={value} year="numeric" month="short" day="numeric" />
    ) : (
      value
    );

    return {
      ...acc,
      [field]: formattedValue,
    };
  }, {}),
});

const transitions = {
  enterTo: 'translate-x-0',
  leaveFrom: 'translate-x-0',
  enterFrom: 'translate-x-full',
  leaveTo: 'translate-x-full',
  enter: 'transition-all duration-300',
  leave: 'transition-all duration-300',
};

const Alert: React.FC<AlertProps> = ({
  title, description, collapse, duration, type = NotificationType.INFO, onClose,
}) => {
  const browserName = Bowser.getParser(window.navigator.userAgent).getBrowserName();
  const platformType = Bowser.getParser(window.navigator.userAgent).getPlatformType();
  const version = Bowser.getParser(window.navigator.userAgent).getBrowserVersion()?.split('.')?.[0];

  const lowPerfMode = useSelector(isLowPerfMode);

  const [isExpanded, setIsExpanded] = React.useState(false);
  const timeout = React.useRef<TimeoutResult>(null);

  const [show, setShow] = React.useState(true);

  const toggleExpand = React.useCallback(() => setIsExpanded((oldIsExpanded) => !oldIsExpanded), []);

  React.useLayoutEffect(() => {
    if (duration) {
      timeout.current = setTimeout(() => setShow(false), duration);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);

        // Safari 13+ doesn't support afterLeave props on Transition component
        if (platformType === 'mobile' && browserName === 'Safari' && parseInt(version, 10) < 14) {
          setTimeout(() => onClose?.(), duration + 300);
        }
      }
    };
  }, [browserName, duration, onClose, platformType, version]);

  return (
    <Transition
      show={show}
      appear
      afterLeave={onClose}
      {...(!lowPerfMode ? transitions : {})}
    >
      <div className="w-72 kiosk:w-96 min-h-9 z-50 rounded-card shadow-md mb-4 border bg-white p-2 pl-0 flex" role="alert">
        <div className="flex justify-center w-1/6 kiosk:mt-1">
          {ALERT_CONFIG[type].icon}
        </div>
        <div className="w-5/6">
          {title && (
            <h5 className="font-bold kiosk:text-xl text-default leading-5">
              {isIntlMessage(title) ? <FormattedMessage {...title} /> : title}
            </h5>
          )}
          {description && (
            <p className="text-xs kiosk:text-base text-low mt-2">
              {isIntlMessage(description)
                ? <FormattedMessage {...descriptionFormatter(description)} />
                : description}
            </p>
          )}
          {collapse && (
            <>
              <div className="flex items-center cursor-pointer" onClick={toggleExpand}>
                <Icon className={
                  classNames(
                    '!text-size-button-xs text-default mr-2',
                    isExpanded ? 'fb-icon--arrow-up' : 'fb-icon--arrow-down',
                  )
                }
                />
                <span className="text-low text-xs kiosk:text-base my-2 underline">
                  {isIntlMessage(collapse.title) ? <FormattedMessage {...collapse.title} /> : collapse.title}
                </span>
              </div>
              {isExpanded && (
                <p className="text-sm">
                  {isIntlMessage(collapse.description)
                    ? (<FormattedMessage {...collapse.description} />)
                    : (collapse.description)}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </Transition>
  );
};

export default Alert;
